import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ContactPhoneCaptureService } from '../onboarding-checklist/contact-phone-capture.service';
import { hasCompanyRole, hasLeadRole, hasManagerRole } from '../utils/role.utils';
import {
  WhatsAppDailySummaryModalComponent,
  WhatsAppDailySummaryModalResult,
} from '../components/whatsapp-daily-summary-modal/whatsapp-daily-summary-modal.component';

export const WA_SUMMARY_PHONE_SNOOZE_KEY = 'supersee_wa_summary_phone_snooze_date';

@Injectable({ providedIn: 'root' })
export class WhatsAppDailySummaryPromptService {
  private isDialogOpened = false;
  private pendingUser: any = null;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private contactPhoneCapture: ContactPhoneCaptureService
  ) {}

  /** Local calendar date YYYY-MM-DD. */
  todayLocalDate(): string {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /** Setup / auth routes — never show popup here (Ready already asks inline). */
  isBlockedRoute(url: string = this.router.url): boolean {
    const path = String(url || '').split('?')[0].split('#')[0].toLowerCase();
    if (!path || path === '/') {
      return true;
    }
    return (
      path === '/setup' ||
      path.startsWith('/setup/') ||
      path.includes('/login') ||
      path.includes('/signup') ||
      path.includes('/verification') ||
      path.includes('/onboarding') ||
      path.includes('/plan-expired')
    );
  }

  /** @deprecated use isBlockedRoute */
  isOnActivationSetupRoute(url: string = this.router.url): boolean {
    return this.isBlockedRoute(url);
  }

  shouldShow(user: any, url: string = this.router.url): boolean {
    if (!user) {
      return false;
    }
    if (!hasCompanyRole(user)) {
      return false;
    }
    if ((hasManagerRole(user) || hasLeadRole(user)) && !hasCompanyRole(user)) {
      return false;
    }
    if (this.contactPhoneCapture.hasSavedPhone(user)) {
      return false;
    }
    const quota = String(user?.meta?.quota_status || '').toLowerCase();
    if (quota !== 'trial') {
      return false;
    }
    if (this.isBlockedRoute(url)) {
      return false;
    }
    if (this.dialog.openDialogs.length > 0) {
      return false;
    }
    const snoozed = localStorage.getItem(WA_SUMMARY_PHONE_SNOOZE_KEY);
    if (snoozed && snoozed === this.todayLocalDate()) {
      return false;
    }
    return true;
  }

  snoozeForToday(): void {
    const untilDate = this.todayLocalDate();
    localStorage.setItem(WA_SUMMARY_PHONE_SNOOZE_KEY, untilDate);
    this.contactPhoneCapture.trackPopupSnoozed(untilDate);
  }

  clearSnooze(): void {
    localStorage.removeItem(WA_SUMMARY_PHONE_SNOOZE_KEY);
  }

  /**
   * Remember user after sync. Actual open happens on NavigationEnd via
   * maybeOpenForCurrentRoute so we never stack on /login → /setup/ready.
   */
  rememberUser(user: any): void {
    this.pendingUser = user;
  }

  maybeOpenForCurrentRoute(url: string = this.router.url): void {
    const user = this.pendingUser;
    if (!user) {
      return;
    }
    this.checkAndOpen(user, url);
  }

  checkAndOpen(user: any, url: string = this.router.url): void {
    this.pendingUser = user;
    if (this.isDialogOpened) {
      return;
    }
    if (!this.shouldShow(user, url)) {
      return;
    }

    this.isDialogOpened = true;
    this.contactPhoneCapture.trackNudgeShown('daily_popup');

    const dialogRef = this.dialog.open(WhatsAppDailySummaryModalComponent, {
      width: '420px',
      maxWidth: '92vw',
      autoFocus: 'dialog',
      restoreFocus: true,
      disableClose: false,
      panelClass: 'wa-daily-summary-dialog',
    });

    dialogRef.afterClosed().subscribe((result: WhatsAppDailySummaryModalResult | undefined) => {
      this.isDialogOpened = false;
      if (result === 'saved') {
        this.clearSnooze();
        return;
      }
      this.snoozeForToday();
    });
  }
}
